<template>
    <div class="vehicle">
        <template v-if="vehicle">
            <div id="sticky-header-container" class="sticky-navbar">
                <div id="sticky-header-menu" class="sticky-navbar__row">
                    <div class="wrap sticky-navbar__wrapper">
                        <ul class="sticky-navbar__submenu">
                            <li v-for="(section, index) in stickyHeaderSections" :key="index">
                                <a
                                    :id="`${section.name}-link`"
                                    :href="generateLink(section)"
                                    @click.prevent="scrollMeTo(section.name)"
                                >
                                    {{ section.translationKey }}
                                </a>
                            </li>
                        </ul>

                        <div
                            v-show="
                                (!isLoggedIn || isRenter) && isStickyPriceInfoVisible && !renterCanNotBookBaloiseVehicle
                            "
                            class="sticky-navbar__price-info"
                        >
                            <div v-if="selectedDates.from">
                                <div v-if="!$v.selectedDates.$invalid" class="sticky-navbar__total-price">
                                    <span>{{ $t('pages.vehicle.sticky_header.total') }}</span>
                                    <VehiclePricing
                                        :vehicle="vehicle"
                                        :show-deposit="vehicle.pricing ? !vehicle.pricing.cashDeposit : false"
                                        :pricing="vehicle.pricing"
                                        :is-loading="isLoadingPrice"
                                        :load-pricing-error="loadPricingError"
                                        :from-date="selectedDates.from"
                                        :to-date="selectedDates.to"
                                        mode="total-only"
                                    />
                                </div>
                                <div v-else>{{ $t('pages.vehicle.sticky_header.unavailable_dates') }}</div>
                                <a class="sticky-navbar__date-range" @click="focusDatepicker">
                                    {{ selectedDatesFormatted }}
                                </a>
                            </div>
                            <strong v-else class="font-weight-bold">{{ nightlyPriceTxt }}</strong>

                            <button
                                v-if="selectedDates.from"
                                :disabled="$v.selectedDates.$invalid || !featureEnabled('renterAccess')"
                                class="button button--primary sticky-navbar__book-btn"
                                @click="checkForOverlappingDates('booking-request-form')"
                            >
                                <template v-if="vehicle.bookingMethod === 'instant'">
                                    {{ $t('booking_request_form.book_instantly') }}
                                </template>
                                <template v-else>{{ $t('booking_request_form.send_request_button') }}</template>
                            </button>
                            <button
                                v-else
                                class="button button--primary sticky-navbar__book-btn"
                                @click="focusDatepicker"
                            >
                                {{ $t('booking_request_form.select_dates_button') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="section section--floating vehicle__section wrap">
                <div v-if="isNewBookingProcessOptimisationEnabled" class="vehicle__header">
                    <h1>{{ vehicleTitle }}</h1>
                    <VehicleLocation
                        :vehicle="vehicle"
                        class="vehicle__location"
                        :show-full-country-name="true"
                        :show-icon="true"
                    />
                </div>
                <VehicleInfo
                    v-if="!isNewBookingProcessOptimisationEnabled"
                    :vehicle="vehicle"
                    @link-clicked="scrollMeTo"
                />
                <VehicleImages id="vehicle-images" class="vehicle__images" :vehicle="vehicle" />
                <div class="vehicle__content">
                    <div class="vehicle__text-content">
                        <VehicleInfoNew
                            v-if="isNewBookingProcessOptimisationEnabled"
                            :vehicle="vehicle"
                            :title="vehicleTitle"
                            :cancellation-free-until="cancellationFreeUntil"
                            @link-clicked="scrollMeTo"
                        />

                        <div v-if="!isNewBookingProcessOptimisationEnabled" class="vehicle__intro">
                            <div class="vehicle__info">
                                <VehicleLocation :vehicle="vehicle" :with-type="true" />
                                <ul class="summary-bullets vehicle__summary-bullets">
                                    <VehicleField type="value" :vehicle="vehicle" field="sleepsNumber" />
                                    <VehicleField type="value" :vehicle="vehicle" field="sitsNumber" />
                                    <VehicleField type="value" :vehicle="vehicle" field="petsAllowed" />
                                </ul>
                            </div>
                        </div>

                        <div v-if="!isNewBookingProcessOptimisationEnabled" class="vehicle__top-info">
                            <div v-for="(item, i) in vehicleTopInfoItems" :key="i" class="vehicle__top-info__item">
                                <div class="item__icon">
                                    <svg-icon :name="item.iconName" />
                                </div>
                                <div class="item__content">
                                    <div class="title">{{ item.title }}</div>
                                    <div class="description">{{ item.description }}</div>
                                </div>
                            </div>
                        </div>

                        <div class="vehicle-section">
                            <h3 class="vehicle-section__heading">
                                {{ $t('pages.vehicle.overview_heading') }}
                            </h3>
                            <VehicleOverviewNew
                                v-if="isNewBookingProcessOptimisationEnabled"
                                :vehicle="vehicle"
                                @click="displayVehicleCharacteristicsModal({ vehicle })"
                            />
                            <VehicleOverview
                                v-else
                                :vehicle="vehicle"
                                @click="displayVehicleCharacteristicsModal({ vehicle })"
                            />
                        </div>

                        <VehicleEquipment
                            id="vehicle-equipment"
                            :vehicle="vehicle"
                            @click="displayVehicleEquipmentModal({ vehicle })"
                        />

                        <VehicleAddons
                            :vehicle="vehicle"
                            :type="TYPE_EQUIPMENT_ADDON"
                            :addons="vehicleAddons"
                            @click="displayVehicleAddonsModal({ vehicleAddons, type: TYPE_EQUIPMENT_ADDON })"
                        />

                        <VehicleAddons
                            :vehicle="vehicle"
                            :type="TYPE_SERVICE_ADDON"
                            :addons="vehicleAddons"
                            @click="displayVehicleAddonsModal({ vehicleAddons, type: TYPE_SERVICE_ADDON })"
                        />

                        <div v-if="vehicle.otherEquipment" class="vehicle-section">
                            <h3 class="vehicle-section__heading">
                                {{ $t('pages.vehicle.other_equipment_subheading') }}
                            </h3>
                            <div>
                                <Notice
                                    v-if="vehicle.language && $i18n.locale !== vehicle.language"
                                    class="vehicle-section__notice"
                                    type="info"
                                >
                                    <p>{{ $t('common.translate_info') }}</p>
                                    <GoogleTranslateLink :text="vehicle.otherEquipment" />
                                </Notice>
                                <TruncatedText
                                    :text="vehicle.otherEquipment"
                                    @click="displayAdditionalEquipmentModal({ vehicle })"
                                />
                            </div>
                        </div>

                        <div class="vehicle-section vehicle__rules">
                            <h3 class="vehicle-section__heading">
                                {{ $t('pages.vehicle.rules_heading') }}
                            </h3>
                            <div>
                                <OverviewListItem :vehicle="vehicle" field="minimumAge" />
                                <li class="overview-list-item">
                                    <template v-if="vehicle.rentersCarParking">
                                        {{ $t('pages.vehicle.parking_available') }}
                                    </template>
                                    <template v-else>{{ $t('pages.vehicle.parking_not_available') }}</template>
                                </li>
                                <OverviewListItem :vehicle="vehicle" field="timeCheckin" />
                                <OverviewListItem :vehicle="vehicle" field="timeCheckout" />
                            </div>
                            <button
                                class="vehicle-section__button button button--secondary-contrast button--small"
                                @click="displayVehicleRulesModal({ vehicle })"
                            >
                                {{ $t('pages.vehicle.show_all_rules') }}
                            </button>
                        </div>

                        <div v-if="vehicle.description" class="vehicle-section">
                            <h3 class="vehicle-section__heading">
                                {{ $t('pages.vehicle.other_info_heading') }}
                            </h3>
                            <div>
                                <Notice
                                    v-if="vehicle.language && $i18n.locale !== vehicle.language"
                                    class="vehicle-section__notice"
                                    type="info"
                                >
                                    <p>{{ $t('common.translate_info') }}</p>
                                    <GoogleTranslateLink :text="vehicle.description" />
                                </Notice>
                                <TruncatedText
                                    :text="vehicle.description"
                                    @click="displayVehicleDescriptionModal({ vehicle })"
                                />
                            </div>
                        </div>

                        <!-- Accidentally added this section (wrong AC in epic), we can just enable it when needed -->
                        <div v-if="false" class="vehicle__about-section">
                            <h3 class="vehicle-section__heading">
                                {{ $t('pages.vehicle.other_info_heading') }}
                            </h3>
                            <div>
                                <TruncatedText
                                    :text="vehicle.description"
                                    :is-show-more-link="true"
                                    @click="displayVehicleDescriptionModal({ vehicle })"
                                />
                                <GoogleTranslateButton :text="vehicle.description" />
                            </div>
                        </div>

                        <div class="vehicle-section">
                            <h3 class="vehicle-section__heading">
                                {{ $t('pages.vehicle.insurance_heading') }}
                            </h3>
                            <VehicleInsuranceNew
                                v-if="isNewBookingProcessOptimisationEnabled"
                                :vehicle="vehicle"
                                :addons="vehicleAddons"
                                :is-omocom-insurance="isOmocomInsurance"
                                :is-baloise-insurance="isBaloiseInsurance"
                                :deductible-values="deductibleValues"
                            />
                            <VehicleInsurance
                                v-else
                                :vehicle="vehicle"
                                :addons="vehicleAddons"
                                :is-omocom-insurance="isOmocomInsurance"
                                :deductible-values="deductibleValues"
                            />
                        </div>

                        <div class="vehicle-section">
                            <h3 class="vehicle-section__heading">
                                {{ $t('pages.vehicle.pricing_heading') }}
                            </h3>
                            <VehiclePrices class="vehicle__prices" :vehicle="vehicle" :addons="vehicleAddons" />
                        </div>

                        <div class="vehicle-section">
                            <h3 class="vehicle-section__heading">
                                {{ $t('pages.vehicle.cancellation_heading') }}
                            </h3>
                            <div>
                                <li class="overview-list-item">
                                    <label class="overview-list-item__label">
                                        {{
                                            $t(`pages.vehicle.${vehicle.cancellationPolicy}_cancellation_policy_label`)
                                        }}
                                    </label>
                                    <span class="overview-list-item__value">
                                        <template v-if="cancellationFreeUntil">
                                            {{
                                                $t('pages.vehicle.free_cancellation_desc', {
                                                    date: cancellationFreeUntil,
                                                })
                                            }}
                                        </template>
                                        <template v-else>
                                            {{
                                                $t(
                                                    `pages.vehicle.${vehicle.cancellationPolicy}_cancellation_policy_desc`
                                                )
                                            }}
                                        </template>
                                    </span>
                                </li>
                                <button
                                    class="vehicle-section__button button button--secondary-contrast button--small"
                                    @click="displayCancellationInfoModal({ vehicle })"
                                >
                                    {{ $t('pages.vehicle.show_cancellation_details') }}
                                </button>
                            </div>
                        </div>

                        <div id="vehicle-owner" class="vehicle-section">
                            <h3 class="vehicle-section__heading">
                                {{ $t('pages.vehicle.owner_info_section_subheading') }}
                            </h3>
                            <VehicleOwnerInfo
                                :full="true"
                                :vehicle="vehicle"
                                :show-send-message="true"
                                @send-message-click="handleSendMessage"
                            />
                        </div>

                        <div id="vehicle-reviews" class="vehicle-section">
                            <h3 class="vehicle-section__heading">
                                {{ $t('pages.vehicle.reviews_heading') }}
                            </h3>
                            <VehicleReviews :vehicle="vehicle" />
                        </div>

                        <div id="vehicle-location" class="vehicle-section">
                            <h3 class="vehicle-section__heading">
                                {{ locationHeading }}
                            </h3>
                            <Notice v-if="isOwner" :show-icon="true" type="info" class="vehicle__location_notice">
                                {{ $t('pages.vehicle.location_owner_notice') }}
                            </Notice>
                            <VehicleMap
                                :show-on-click="true"
                                :center="mapCenter"
                                :vehicles="[vehicle]"
                                :zoom-level="11"
                                :country="vehicle && vehicle.country"
                                marker-type="simple"
                                class="vehicle__map"
                            />
                        </div>
                    </div>
                </div>
                <div class="vehicle__sidebar">
                    <div class="vehicle__sidebar-container">
                        <LoadingIndicator v-if="!featureFlagsLoaded" />
                        <template v-else>
                            <BookingRequestForm
                                v-if="isNewBookingProcessOptimisationEnabled"
                                id="booking-request-form"
                                :vehicle="vehicle"
                                :renter-can-not-book-baloise-vehicle="renterCanNotBookBaloiseVehicle"
                                :not-allowed-to-rent-error="notAllowedToRentError"
                                :is-loading-price="isLoadingPrice"
                                :load-pricing-error="loadPricingError"
                                class="vehicle__booking-request"
                                @click="checkForOverlappingDates"
                            />
                            <!-- Remove after the new booking process optimisation is fully deployed -->
                            <BookingRequestFormOld
                                v-else
                                id="booking-request-form"
                                :vehicle="vehicle"
                                :renter-can-not-book-baloise-vehicle="renterCanNotBookBaloiseVehicle"
                                :not-allowed-to-rent-error="notAllowedToRentError"
                                :is-loading-price="isLoadingPrice"
                                :load-pricing-error="loadPricingError"
                                class="vehicle__booking-request"
                                @click="checkForOverlappingDates"
                            />

                            <div
                                v-if="vehicle.hasPlatformInsurance && !isNewBookingProcessOptimisationEnabled"
                                class="vehicle__summary-insurance"
                            >
                                <div class="vehicle__summary-insurance__heading">
                                    <span>{{ $t('pages.vehicle.platform_insurance_heading') }}</span>
                                    <InsuranceLogo :vendor="vehicle.insuranceVendor" class="logo" />
                                </div>
                                <div class="vehicle__summary-insurance__content">
                                    <div class="icon-wrapper">
                                        <svg-icon name="transport" />
                                    </div>
                                    <span v-if="isBaloiseInsurance">{{
                                        $t('pages.vehicle.platform_insurance_baloise_description')
                                    }}</span>
                                    <span v-else>{{ $t('pages.vehicle.platform_insurance_description') }}</span>
                                </div>
                                <div class="vehicle__summary-insurance__content">
                                    <div class="icon-wrapper">
                                        <svg-icon name="discuss" />
                                    </div>
                                    <span v-if="isBaloiseInsurance">{{
                                        $t('pages.vehicle.platform_insurance_baloise_description2')
                                    }}</span>
                                    <span v-else>{{
                                        $t('pages.vehicle.platform_insurance_description2', {
                                            roadsideAssistanceCompany: roadsideAssistanceCompany,
                                        })
                                    }}</span>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
                <VehicleMobileFooter
                    :vehicle="vehicle"
                    :not-allowed-to-rent-error="notAllowedToRentError"
                    :renter-can-not-book-baloise-vehicle="renterCanNotBookBaloiseVehicle"
                    class="vehicle__footer"
                    :class="{ 'is-visible': showFooter }"
                    @click="checkForOverlappingDates"
                />
            </div>

            <div id="related-vehicles" class="section vehicle__related">
                <h2 class="section__heading">
                    {{ $t('pages.vehicle.related_vehicles_heading') }}
                </h2>
                <RelatedVehiclesList :id="vehicle.id" :count="4" />
            </div>

            <RentalProcess />
        </template>
    </div>
</template>

<router>
{
    name: 'vehicle'
}
</router>

<script>
import { mapGetters } from 'vuex';

import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import parseISO from 'date-fns/parseISO';
import getYear from 'date-fns/getYear';
import startOfDay from 'date-fns/startOfDay';
import differenceInDays from 'date-fns/differenceInDays';
import subDays from 'date-fns/subDays';
import { actionTypes as navigationActions } from '~/store/navigation';
import modalMixin from '~/mixins/modalMixin';
import { getFilterValue, buildQuery } from '~/helpers/queryParser';
import { AVAILABLE_BETWEEN } from '~/config/filterKeys';
import { PLATFORM_INSURANCE, TYPE_EQUIPMENT_ADDON, TYPE_SERVICE_ADDON } from '~/config/productTypes';
import { getVehicleFieldFormattedValue, getVehicleFieldValue } from '~/config/vehicleFields';

import VehicleImages from '~/components/vehicles/VehicleImages.vue';
import VehicleReviews from '~/components/vehicle/VehicleReviews.vue';
import RelatedVehiclesList from '~/components/vehicle/RelatedVehiclesList.vue';
import RentalProcess from '~/components/sections/RentalProcess.vue';
import InsuranceLogo from '~/components/InsuranceLogo.vue';
import Notice from '~/components/Notice.vue';
import VehicleMap from '~/components/vehicles/VehicleMap.vue';

import VehicleOverview from '~/components/vehicle/VehicleOverview.vue';
import VehicleEquipment from '~/components/vehicle/VehicleEquipment.vue';
import VehiclePrices from '~/components/vehicle/VehiclePrices.vue';
import VehicleInsurance from '~/components/vehicle/VehicleInsurance.vue';
import VehicleOwnerInfo from '~/components/vehicle/VehicleOwnerInfo.vue';
import VehicleMobileFooter from '~/components/vehicle/VehicleMobileFooter.vue';

import {
    ASSET_SUB_TYPE_CARAVAN,
    ASSET_SUB_TYPE_CAMPERVAN,
    ASSET_SUB_TYPE_MOTORHOME,
    ASSET_SUB_TYPE_OTHER_CAMPER,
} from '~/config/assetTypes';
import OverviewListItem from '~/components/vehicle/OverviewListItem.vue';
import GoogleTranslateLink from '~/components/GoogleTranslateLink.vue';
import VehiclePricing from '~/components/vehicles/VehiclePricing';
import VehicleInfo from '~/components/vehicles/VehicleInfo';
import VehicleInfoNew from '~/components/vehicles/VehicleInfoNew';
import { DRAFT, ACTIVE, CONFIRMED, PENDING_CONFIRMATION } from '~/config/bookingStatuses';
import { INSTANT } from '~/config/bookingMethods';
import { CANCELLATION_POLICIES, CANCELLATION_THRESHOLDS } from '~/config/bookingCancellation';
import { actionTypes as userActions } from '~/store/user';
import VehicleLocation from '~/components/vehicles/VehicleLocation';
import VehicleField from '~/components/vehicle/VehicleField';
import TruncatedText from '~/components/TruncatedText';
import VehicleAddons from '~/components/vehicle/VehicleAddons';
import BookingRequestForm from '~/components/booking/BookingRequestForm.vue';
import BookingRequestFormOld from '~/components/booking/BookingRequestFormOld.vue'; // dynamic imports cause appendChild error. Import both for now.
import LoadingIndicator from '~/components/LoadingIndicator.vue';
import GoogleTranslateButton from '~/components/vehicles/GoogleTranslateButton';
import VehicleOverviewNew from '~/components/vehicle/VehicleOverviewNew';
import VehicleInsuranceNew from '~/components/vehicle/VehicleInsuranceNew';

export default {
    name: 'SingleVehicle',
    components: {
        VehicleInsuranceNew,
        VehicleOverviewNew,
        GoogleTranslateButton,
        VehicleAddons,
        VehicleField,
        VehicleLocation,
        TruncatedText,
        Notice,
        VehicleInfo,
        VehiclePricing,
        VehicleImages,
        RelatedVehiclesList,
        VehicleReviews,
        VehicleOverview,
        VehicleEquipment,
        VehiclePrices,
        VehicleInsurance,
        RentalProcess,
        VehicleMap,
        InsuranceLogo,
        VehicleOwnerInfo,
        VehicleMobileFooter,
        OverviewListItem,
        GoogleTranslateLink,
        BookingRequestForm,
        BookingRequestFormOld,
        LoadingIndicator,
        VehicleInfoNew,
    },
    mixins: [validationMixin, modalMixin],
    validations: {
        selectedDates: {
            from: {
                required,
                validRange() {
                    return this.$validators.isValidRange(
                        this.vehicle.unavailableDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                minLength() {
                    return this.$validators.isRangeLongEnough(
                        this.minBookingLengthForDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                maxLength() {
                    return this.$validators.isRangeShorterThanMax(this.selectedDates.from, this.selectedDates.to);
                },
            },
            to: {
                required,
                validRange() {
                    return this.$validators.isValidRange(
                        this.vehicle.unavailableDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                minLength() {
                    return this.$validators.isRangeLongEnough(
                        this.minBookingLengthForDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                maxLength() {
                    return this.$validators.isRangeShorterThanMax(this.selectedDates.from, this.selectedDates.to);
                },
            },
        },
    },
    async asyncData({ app, store, route, redirect }) {
        try {
            const { from, to } = getFilterValue(route.query, AVAILABLE_BETWEEN);

            const vehicle = await store.$vehicleService.getVehicle(route.params.id, from, to);
            const vehicleAddons = await store.$vehicleService.getAvailableAddons(
                route.params.id,
                from,
                to,
                null,
                vehicle.isConsideredSwiss && vehicle.countryAllowsPlatformInsurance
            );

            // Mark the vehicle type's corresponding menu item as active
            const subTypeMenuItems = {
                [ASSET_SUB_TYPE_MOTORHOME]: 'motorhomes-page',
                [ASSET_SUB_TYPE_CAMPERVAN]: 'motorhomes-page',
                [ASSET_SUB_TYPE_OTHER_CAMPER]: 'motorhomes-page',
                [ASSET_SUB_TYPE_CARAVAN]: 'caravans-page',
            };

            store.dispatch(navigationActions.MARK_ITEM_ACTIVE, subTypeMenuItems[vehicle.subType]);

            return { vehicle, vehicleAddons };
        } catch (err) {
            store.$sentry.captureException(err);

            if (err?.slug === 'model_not_found') {
                if (!err?.meta) {
                    return redirect(
                        301,
                        app.localePath({
                            path: `/${app.i18n.t('common.motorhomes_page_path')}`,
                            query: { [app.i18n.t('common.all_vehicles_query')]: 1 },
                        })
                    );
                }

                const { sub_type: subType } = err.meta;
                let path = '';

                switch (subType) {
                    case ASSET_SUB_TYPE_MOTORHOME:
                        path = `/${app.i18n.t('common.motorhomes_page_path')}`;
                        break;
                    case ASSET_SUB_TYPE_CAMPERVAN:
                        path = `/${app.i18n.t('common.campervans_page_path')}`;
                        break;
                    case ASSET_SUB_TYPE_CARAVAN:
                        path = `/${app.i18n.t('common.caravans_page_path')}`;
                        break;
                    default:
                        path = `/${app.i18n.t('common.motorhomes_page_path')}`;
                }

                return redirect(
                    301,
                    app.localePath({
                        path,
                    })
                );
            }

            throw err;
        }
    },
    data() {
        return {
            showFooter: false,
            isStickyPriceInfoVisible: false,
            isLoadingPrice: false,
            loadPricingError: false,
            notAllowedToRentError: '',
            vehicle: null,
            initialGTMFilter: true,
            vehicleAddons: null,

            TYPE_EQUIPMENT_ADDON,
            TYPE_SERVICE_ADDON,
        };
    },
    async fetch() {
        try {
            await this.$store.dispatch(userActions.FETCH_ID_VERIFICATION);
        } catch (err) {
            this.$sentry.captureException(err);
        }
    },
    head() {
        const meta = [
            {
                hid: 'description',
                name: 'description',
                content: this.pageDescription,
            },
            { hid: 'og-title', property: 'og:title', content: this.pageTitle },
            { hid: 'og-description', property: 'og:description', content: this.pageDescription },
        ];

        const firstImage = this.getFirstImage();

        if (firstImage) {
            meta.push({ hid: 'og-image', property: 'og:image', content: firstImage.url });
            meta.push({ hid: 'og-image-secure', property: 'og:image:secure_url', content: firstImage.url });
        }

        return {
            title: this.pageTitle,
            meta,
            link: [...this.getImagePreloadLinks()],
        };
    },
    nuxtI18n: {
        paths: {
            se: '/fordon/:id',
            'fi-sv': '/fordon/:id',
            en: '/vehicles/:id',
            de: '/fahrzeuge/:id',
            at: '/fahrzeuge/:id',
            fi: '/ajoneuvo/:id',
            no: '/kjoretoy/:id',
            dk: '/koeretoejer/:id',
            ch: '/fahrzeuge/:id',
            fr: '/vehicules/:id',
        },
    },
    computed: {
        ...mapGetters([
            'user',
            'isLoggedIn',
            'isOwner',
            'isRenter',
            'hasPendingMessage',
            'pendingMessage',
            'isLargeScreen',
            'isPendingIdVerification',
            'userCanBuyBaloiseInsurance',
            'featureFlagsLoaded',
            'isNewBookingProcessOptimisationEnabled',
        ]),
        vehicleTitle() {
            const ownerName = this.vehicle.owner?.isOrganizationRepresentative ? '' : this.vehicle.owner.firstName;

            return this.$tc('vehicle_info.title', ownerName ? 2 : 1, {
                vehicleBrand: getVehicleFieldValue(this.vehicle, 'brand'),
                type: getVehicleFieldFormattedValue(this.vehicle, 'subType'),
                ownerName,
            });
        },
        roadsideAssistanceCompany() {
            return this.vehicle.insuranceVendor?.roadsideAssistanceCompany;
        },
        platformInsuranceCompany() {
            if (this.vehicle.insuranceVendor?.name) {
                return this.vehicle.insuranceVendor?.name.toLowerCase();
            }

            return null;
        },
        selectedDates() {
            return getFilterValue(this.$route.query, AVAILABLE_BETWEEN);
        },
        selectedDatesFormatted() {
            const { dateFormat } = this.$options.filters;
            const start = dateFormat(this.selectedDates.from, 'd MMM');
            let end = dateFormat(this.selectedDates.to, 'd MMM');
            const startYear = getYear(parseISO(this.selectedDates.from));
            const endYear = getYear(parseISO(this.selectedDates.to));

            if (startYear !== endYear) {
                end += ` ${endYear}`;
            }

            return `${start} - ${end}`;
        },
        minBookingLengthForDates() {
            // NOTE: This code is duplicated in the BookingRequest view
            const { from, to } = this.selectedDates;

            if (this.$utils.isOnlyHighSeason(from, to)) {
                return this.vehicle.minBookingLengthHigh;
            }

            if (this.$utils.isOnlyLowSeason(from, to)) {
                return this.vehicle.minBookingLengthLow;
            }

            // Mixed season, take the "loosest" rule
            return Math.min(this.vehicle.minBookingLengthHigh, this.vehicle.minBookingLengthLow);
        },
        nightlyPriceTxt() {
            const { pricing } = this.vehicle;

            const nightlyPrice = pricing.lowNightlyPrice;

            const amount = this.$root.$options.filters.price(nightlyPrice) || 0;
            const formatted = this.$root.$options.filters.formatAsPrice(amount, pricing.currency, 0);

            return this.$t('pages.vehicle.sticky_header.nightly_price', { price: formatted });
        },
        stickyHeaderSections() {
            return [
                { name: 'vehicle-images', translationKey: this.$t('pages.vehicle.sticky_header.photos') },
                { name: 'vehicle-equipment', translationKey: this.$t('pages.vehicle.sticky_header.equipment') },
                { name: 'vehicle-reviews', translationKey: this.$t('pages.vehicle.sticky_header.reviews') },
                { name: 'vehicle-location', translationKey: this.$t('pages.vehicle.sticky_header.location') },
            ];
        },
        isVehicleMotorized() {
            return (
                this.isVehicleMotorhome ||
                this.isVehicleCampervan ||
                (this.isVehicleTypeOther && this.vehicle.isMotorized)
            );
        },
        isVehicleCampervan() {
            return this.vehicle.subType === ASSET_SUB_TYPE_CAMPERVAN;
        },
        isVehicleMotorhome() {
            return this.vehicle.subType === ASSET_SUB_TYPE_MOTORHOME;
        },
        isVehicleTypeOther() {
            return this.vehicle.subType === ASSET_SUB_TYPE_OTHER_CAMPER;
        },
        mapCenter() {
            if (!this.vehicle) {
                return null;
            }

            return { lat: this.vehicle.latitude, lng: this.vehicle.longitude };
        },
        locationHeading() {
            if (this.isVehicleMotorhome) {
                return this.$t('pages.vehicle.location_heading_motorhome');
            } else if (this.isVehicleCampervan) {
                return this.$t('pages.vehicle.location_heading_campervan');
            } else if (this.isVehicleTypeOther) {
                return this.$t('pages.vehicle.location_heading_other_camper');
            }

            return this.$t('pages.vehicle.location_heading_caravan');
        },
        vehicleType() {
            if (this.isVehicleCampervan) {
                return this.$tc('common.campervan', 1);
            } else if (this.isVehicleMotorhome) {
                return this.$tc('common.motorhome', 1);
            } else if (this.isVehicleTypeOther) {
                return this.$tc('common.other_camper', 1);
            }

            return this.$tc('common.caravan', 1);
        },
        pageTitle() {
            if (!this.vehicle) {
                return null;
            }

            const { city, sleepsNumber, pricing } = this.vehicle;
            const amount = this.$root.$options.filters.price(pricing.lowNightlyPrice) || 0;
            const priceFrom = this.$root.$options.filters.formatAsPrice(amount, this.vehicle.currency, 0);
            const vehicleType = this.vehicleType;

            return this.$t('pages.vehicle.seo_page_title', {
                vehicleType,
                city,
                sleepsNumber,
                fromXPerNight: this.$t('pricing.from_x_per_night', { price: priceFrom }),
            });
        },
        pageDescription() {
            if (!this.vehicle) {
                return null;
            }

            const { model, brand, modelYear, city, sleepsNumber } = this.vehicle;

            return this.$t('pages.vehicle.seo_page_description', {
                model,
                brand: brand?.name,
                modelYear,
                city,
                sleepsNumber,
            });
        },
        isOldEnough() {
            if (!this.user?.age) {
                return true;
            }

            return this.user.age >= this.vehicle.minimumAge;
        },
        meetsDrivingLicenseRequirements() {
            if (!this.user?.drivingLicenseClass) {
                return true;
            }

            if (this.isVehicleMotorized && this.vehicle.maximumAuthorizedMass > 3500) {
                return this.user.drivingLicenseClass === 'C1';
            }

            return true;
        },
        cancellationFreeUntil() {
            if (!this.selectedDates?.from || this.vehicle.cancellationPolicy === CANCELLATION_POLICIES.STRICT) {
                return null;
            }

            const startDate = parseISO(this.selectedDates.from);

            const today = startOfDay(new Date());
            const daysUntilStart = differenceInDays(startDate, today);

            if (daysUntilStart > CANCELLATION_THRESHOLDS.LONG_NOTICE) {
                return this.$options.filters.fullDateFormat(
                    subDays(startDate, CANCELLATION_THRESHOLDS.LONG_NOTICE_CUTOFF)
                );
            }

            return null;
        },
        vehicleTopInfoItems() {
            const items = [
                {
                    iconName: this.vehicle.bookingMethod === INSTANT ? 'instant' : 'send',
                    title: this.$t(`pages.vehicle.${this.vehicle.bookingMethod}_booking_label`),
                    description: this.$t(`pages.vehicle.${this.vehicle.bookingMethod}_booking_desc`),
                },
            ];

            if (this.vehicle.owner.topOwner) {
                items.push({
                    iconName: 'top_owner',
                    title: this.$t('pages.vehicle.top_owner_label'),
                    description: this.$t('pages.vehicle.top_owner_desc'),
                });
            }

            if (this.cancellationPolicyItem) {
                items.push(this.cancellationPolicyItem);
            }

            return items;
        },
        cancellationPolicyItem() {
            if (this.selectedDates?.from && this.cancellationFreeUntil) {
                return {
                    iconName: 'cancellation',
                    title: this.$t('pages.vehicle.free_cancellation_label', { date: this.cancellationFreeUntil }),
                    description: this.$t('pages.vehicle.free_cancellation_desc', {
                        date: this.cancellationFreeUntil,
                    }),
                };
            }

            if (this.vehicle.cancellationPolicy !== CANCELLATION_POLICIES.STRICT && !this.selectedDates?.from) {
                return {
                    iconName: 'cancellation',
                    title: this.$t(`pages.vehicle.${this.vehicle.cancellationPolicy}_cancellation_policy_label`),
                    description: this.$t(`pages.vehicle.${this.vehicle.cancellationPolicy}_cancellation_policy_desc`),
                };
            }

            return null;
        },
        isBaloiseInsurance() {
            return this.vehicle.hasPlatformInsurance && this.vehicle.isConsideredSwiss;
        },
        isOmocomInsurance() {
            return this.vehicle.hasPlatformInsurance && this.vehicle.isOmocomMarket;
        },
        omocomCoverageProducts() {
            if (!this.isOmocomInsurance) {
                return [];
            }

            return this.vehicleAddons?.filter(a => a.type === PLATFORM_INSURANCE);
        },
        deductibleValues() {
            if (!this.omocomCoverageProducts.length) {
                return;
            }

            const minDeductible = Math.min(...this.omocomCoverageProducts.map(p => p.specifications?.deductibleAmount));
            const maxDeductible = Math.max(...this.omocomCoverageProducts.map(p => p.specifications?.deductibleAmount));

            if (!minDeductible || !maxDeductible) {
                return;
            }

            return {
                minDeductible: this.formattedPrice(minDeductible, this.vehicle.currency, 0),
                maxDeductible: this.formattedPrice(maxDeductible, this.vehicle.currency, 0),
            };
        },
        userNeedsToVerify() {
            return this.user && !this.user.isIdVerified && !this.isPendingIdVerification && this.vehicle.isOmocomMarket;
        },
        renterCanNotBookBaloiseVehicle() {
            return this.$utils.renterCanNotBookBaloiseVehicle(this.vehicle);
        },
    },
    watch: {
        selectedDates: {
            handler() {
                this.loadPricing();

                if (this.$utils.vehicleCanHaveBaloiseInsurance(this.vehicle)) {
                    this.getAvailableAddons();
                }

                this.dispatchDatesTrackingEvent();
            },
            deep: true,
        },
        isLoggedIn(newValue) {
            if (newValue && this.hasPendingMessage) {
                this.handleSendMessage();
            }

            this.checkShouldOpenInsuranceLimitationModal();
        },
    },
    created() {
        if (this.isLoggedIn && this.hasPendingMessage) {
            const messageData = this.pendingMessage;

            if (messageData.recipientId === this.vehicle.owner.id) {
                this.handleSendMessage();
            }
        }

        const isBackFromBookingRequestPage =
            this.$nuxt.context.from?.name && /vehicle-id-book/.test(this.$nuxt.context.from.name);

        if (
            isBackFromBookingRequestPage &&
            (!this.$v.selectedDates.from.validRange || !this.$v.selectedDates.to.validRange)
        ) {
            this.$router.replace(
                this.localePath({
                    name: 'vehicle',
                    params: { id: this.$route.params.id },
                })
            );
        }
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.handleScroll);
    },
    mounted() {
        if (this.vehicle) {
            this.dispatchVehicleGTMEvent('view_item');
            this.checkShouldOpenInsuranceLimitationModal();

            // Reload the pricing to make sure it's up to date even for cached pages
            this.loadPricing();
        }

        window.addEventListener('scroll', this.handleScroll);
    },
    methods: {
        dispatchDatesTrackingEvent() {
            if (!this.selectedDates.from || !this.selectedDates.to) {
                return;
            }

            const start = this.$options.filters.dateFormat(this.selectedDates.from, 'dd.MM.');
            const end = this.$options.filters.dateFormat(this.selectedDates.to, 'dd.MM.yyyy');

            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event: 'traveldate_filter',
                    method: 'pdp',
                    action: this.initialGTMFilter ? 'initial_filter' : 'changed_filter',
                    label: `${start}-${end}`,
                    item_id: this.vehicle.id,
                });
            }

            this.initialGTMFilter = false;
        },
        loadPricing() {
            this.isLoadingPrice = true;

            this.$vehicleService
                .getVehicle(this.vehicle.id, this.selectedDates.from, this.selectedDates.to)
                .then(vehicle => {
                    this.vehicle = vehicle;
                })
                .catch(err => {
                    this.loadPricingError = true;
                    this.$sentry.captureException(err);
                })
                .finally(() => {
                    this.isLoadingPrice = false;
                });
        },
        async getAvailableAddons() {
            try {
                this.vehicleAddons = await this.$vehicleService.getAvailableAddons(
                    this.vehicle.id,
                    this.selectedDates.from,
                    this.selectedDates.to,
                    null,
                    this.$utils.vehicleCanHaveBaloiseInsurance(this.vehicle)
                );
            } catch (err) {
                this.$sentry.captureException(err);
            }
        },
        generateLink(section) {
            const fullPath = this.$route.fullPath.replace(/#.*/, ''); // remove hash value

            return this.$config.siteUrl ? `${this.$config.siteUrl}${fullPath}#${section.name}` : `#${section.name}`;
        },
        handleScroll() {
            if (window.innerWidth > 991) {
                this.showSubmenu();
                this.highlightStickyNavLinks();
                this.isStickyPriceInfoVisible = !this.$utils.isVisibleInViewport('booking-form-btn-wrapper', 90);
                this.showFooter = false;
            } else {
                const relatedVehiclesEl = document.getElementById('related-vehicles');

                // Show the mobile footer when the user is scrolling above the Related vehicles section
                if (relatedVehiclesEl) {
                    const scrollPosition = window.scrollY || window.pageYOffset;
                    const { top } = relatedVehiclesEl.getBoundingClientRect();
                    this.showFooter = scrollPosition + window.innerHeight < top + window.scrollY;
                }
            }
        },
        handleSendMessage() {
            const { owner } = this.vehicle;

            let recipientName = owner.firstName;

            if (owner.isOrganizationRepresentative) {
                recipientName = owner.organizationName;
            }

            const openedFromRoute = {
                name: this.getRouteBaseName(),
                params: this.$route.params,
                query: this.$route.query,
            };

            this.openMessageModal(owner, recipientName, openedFromRoute);
        },
        openMessageModal(owner, recipientName, openedFromRoute) {
            this.displaySendMessageModal({
                recipientId: owner.id,
                isRecipientBlockedInBackgroundChecks: owner.isBlockedInBackgroundChecks,
                recipientName,
                vehicleId: this.vehicle.id,
                openedFromRoute,
            });
        },
        scrollMeTo(id) {
            const element = document.getElementById(id);

            if (!element) {
                return;
            }

            const top = element.offsetTop;
            const offset = window.innerWidth < 992 ? 70 : -20;

            window.scrollTo({
                top: top + offset,
                left: 0,
                behavior: 'smooth',
            });
        },
        checkIfCanRent() {
            if (!this.isLoggedIn) {
                return;
            }

            if (!this.meetsDrivingLicenseRequirements) {
                return this.$t('pages.booking_request.driving_license_requirements_not_met_notice');
            } else if (!this.isOldEnough) {
                return this.$t('pages.booking_request.minimum_age_requirements_not_met', {
                    minimumAge: this.vehicle.minimumAge,
                });
            } else if (this.renterCanNotBookBaloiseVehicle) {
                return this.$t('pages.booking_request.baloise_vehicle_renting_disallowed_notice');
            }

            return null;
        },
        dispatchVehicleGTMEvent(eventName) {
            const { price, dateFormat } = this.$root.$options.filters;
            const { pricing, owner } = this.vehicle;

            const start = this.selectedDates.from && dateFormat(this.selectedDates.from, 'dd.MM.');
            const end = this.selectedDates.to && dateFormat(this.selectedDates.to, 'dd.MM.yyyy');
            const totalPrice = pricing.totalPrice && Math.round(price(pricing.totalPrice));
            const weeklyPrice = this.$utils.isDateHighSeason(this.selectedDates.from)
                ? pricing.highWeeklyPrice
                : pricing.lowWeeklyPrice;
            const pricePerNight = pricing.totalPrice
                ? pricing.totalPrice / pricing.numberOfNights
                : pricing.lowNightlyPrice;
            const ownerName = owner?.isOrganizationRepresentative ? '' : ` of ${owner.firstName}`;

            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event: eventName,
                    ecommerce: {
                        currency: pricing?.currency,
                        value: totalPrice || Math.round(price(weeklyPrice)),
                        items: [
                            {
                                booking_dates: start ? `${start}-${end}` : null,
                                quantity: pricing.numberOfNights || 7,
                                item_id: this.vehicle.id,
                                item_name: `${this.vehicle.brand?.name} ${this.vehicle.subType}${ownerName}`,
                                item_owner_status: owner?.topOwner ? 'top owner' : 'regular',
                                item_brand: this.vehicle.brand?.name,
                                item_category: this.vehicle.type,
                                item_category2: this.vehicle.subType,
                                item_country: this.$t(`localization.countries.${this.vehicle.country}`, 'en'),
                                price: Math.round(price(pricePerNight)),
                                item_cancellation_policy: this.vehicle.cancellationPolicy,
                                item_booking_method: this.vehicle.bookingMethod,
                                item_number_of_reviews: this.vehicle.reviewCount,
                                item_rating: this.vehicle.averageRating || 0,
                                locale: this.$i18n.locale,
                            },
                        ],
                    },
                });
            }
        },
        async checkForOverlappingDates(scrollTo) {
            this.notAllowedToRentError = this.checkIfCanRent();

            if (this.notAllowedToRentError) {
                if (scrollTo) {
                    this.scrollMeTo(scrollTo);
                }

                return;
            }

            const config = {
                ecommerce_statuses: `${DRAFT},${PENDING_CONFIRMATION},${CONFIRMED},${ACTIVE}`,
                from: this.selectedDates.from,
                to: this.selectedDates.to,
                include: '',
            };
            let bookings = [];

            if (this.isLoggedIn) {
                try {
                    bookings = await this.$bookingService.getBookings(config);
                } catch (error) {
                    this.$sentry.captureException(error);
                }
            }

            if (bookings.length > 0) {
                this.displayOverlappingDatesModal().then(this.goToBookingRequest);
            } else {
                this.goToBookingRequest();
            }
        },
        goToBookingRequest() {
            this.dispatchVehicleGTMEvent('add_to_cart');

            const query = buildQuery(this.$route.query, {
                [AVAILABLE_BETWEEN]: this.selectedDates,
            });

            this.$router.push(
                this.localePath({
                    name: 'vehicle-id-book',
                    params: { id: this.vehicle.id },
                    query,
                })
            );
        },
        focusDatepicker() {
            this.$root.$emit('focus-datepicker');
            this.scrollMeTo('booking-request-form');
        },
        showSubmenu() {
            const container = document.getElementById('sticky-header-container');
            const menu = document.getElementById('sticky-header-menu');

            if (!container || !menu) {
                return;
            }

            if (window.scrollY > 120) {
                container.classList.add('visible');
                setTimeout(() => {
                    menu.classList.add('visible');
                });
            } else {
                menu.classList.remove('visible');
                container.classList.remove('visible');
            }
        },
        highlightStickyNavLinks() {
            this.stickyHeaderSections.forEach(({ name }) => {
                const section = document.getElementById(name);

                if (section) {
                    const sectionHeight = section.offsetHeight;
                    const sectionTop = section.offsetTop - 50;

                    if (window.scrollY > sectionTop && window.scrollY <= sectionTop + sectionHeight) {
                        document.getElementById(`${name}-link`).classList.add('active');
                    } else {
                        document.getElementById(`${name}-link`).classList.remove('active');
                    }
                }
            });
        },
        getFirstImage() {
            if (
                !this.vehicle ||
                !this.vehicle.images ||
                !Array.isArray(this.vehicle.images) ||
                this.vehicle.images.length === 0
            ) {
                return null;
            }

            return this.vehicle.images[0];
        },
        getImagePreloadLinks() {
            const firstImage = this.getFirstImage();

            if (!firstImage) {
                return [];
            }

            const { webpSizes, sizes } = firstImage;

            const preloads = [
                {
                    rel: 'preload',
                    as: 'image',
                    href: sizes.large,
                    fetchpriority: 'high',
                    imagesrcset: `${sizes.card} 400w, ${sizes.medium} 800w, ${sizes.large} 1100w`,
                    imagesizes: '(max-width: 992px) 100vw, 1100px',
                },
            ];

            if (webpSizes && webpSizes.full) {
                preloads.push({
                    rel: 'preload',
                    as: 'image',
                    href: webpSizes.large,
                    type: 'image/webp',
                    fetchpriority: 'high',
                    imagesrcset: `${webpSizes.card} 400w, ${webpSizes.medium} 800w, ${webpSizes.large} 1100w`,
                    imagesizes: '(max-width: 992px) 100vw, 1100px',
                });
            }

            this.vehicle.images.forEach((image, index) => {
                if (index > 0 && index < 5) {
                    const sizes = image.sizes;
                    const preload = {
                        rel: 'preload',
                        as: 'image',
                        href: sizes.medium,
                        fetchpriority: 'high',
                        media: '(min-width: 992px)',
                    };

                    preloads.push(preload);

                    const webpSizes = image.webpSizes;

                    if (webpSizes && webpSizes.medium) {
                        preloads.push({
                            rel: 'preload',
                            as: 'image',
                            href: webpSizes.medium,
                            type: 'image/webp',
                            fetchpriority: 'high',
                            media: '(min-width: 992px)',
                        });
                    }
                }
            });

            return preloads;
        },
        checkShouldOpenInsuranceLimitationModal() {
            if (this.renterCanNotBookBaloiseVehicle) {
                this.displayInsuranceLimitationModal({ vehicle: this.vehicle }, true);
                this.notAllowedToRentError = this.$t('pages.booking_request.baloise_vehicle_renting_disallowed_notice');
            }
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

.sticky-navbar {
    display: none;
    z-index: $z-sticky-header;
    height: 0;

    &.visible {
        display: block;
        position: sticky;
        top: 0;
    }

    @media (max-width: $bp-lg-min) {
        display: none !important;
    }

    &__row {
        position: relative;
        top: -90px;
        width: 100%;
        height: 90px;
        background-color: $white;
        transition: top 150ms ease-in-out;
        display: flex;
        align-items: center;
        box-sizing: border-box;
        border-bottom: 1px solid $border-color;

        &.visible {
            top: 0;
        }
    }

    &__wrapper {
        display: flex;
        align-items: center;
        justify-content: space-between;
        @include font-size(16px);
    }

    &__price-info {
        flex-shrink: 0;
        display: flex;
        align-items: center;
    }

    &__total-price {
        .vehicle-pricing {
            display: inline-block;
            font-weight: bold;
            width: auto;
            margin-left: 4px;
            @include font-size(16px);
        }

        .loading-indicator {
            margin-top: -8px;
        }

        span {
            @include font-size(14px);
        }
    }

    &__date-range {
        margin-top: 8px;
        display: inline-block;
        text-decoration: underline !important;
        color: $primary;
        @include font-size(14px);
    }

    &__book-btn {
        margin-left: 16px;
    }

    &__submenu {
        display: flex;
        margin: 0;
        padding: 0;

        li {
            display: inline-block;
            white-space: nowrap;
            width: 100%;

            &:not(:last-child) {
                margin-right: 48px;
            }

            &,
            a {
                color: $primary;
                font-weight: 600;
                cursor: pointer;
                text-decoration: none;

                &.active {
                    color: $blue;
                }
            }
        }
    }
}

.vehicle {
    background: $white;
    @include font-size(16px);

    &__section.section {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;

        padding-top: 0;
        @include sm {
            padding-top: 50px;
        }
    }

    h1 {
        @extend %heading-1;
        margin-bottom: 24px;
    }

    &__header {
        display: none;

        @include lg {
            display: block;
            margin-bottom: 24px;
        }
    }

    &__intro {
        .vehicle-check-availability {
            max-width: unset;
            margin: 0;
            padding: 0;

            &__label,
            &__value,
            a {
                @include font-size(16px);
            }
        }
    }

    &__about-section {
        text-align: left;
        padding-bottom: 32px;

        .button {
            margin-top: 16px;
        }
    }

    &__info {
        display: none;

        @include lg {
            padding: 10px 10px 32px;
            display: block;
        }
    }

    &__images {
        margin-bottom: 24px;

        @include md {
            margin-bottom: 40px;
        }

        @media screen and (max-width: $bp-lg-min) {
            order: -1;
        }
    }

    &__text-content {
        margin: auto;
    }

    &__content {
        width: 100%;
        @include lg {
            margin-right: 20px;
            flex: 0 0 68%;
            max-width: 68%;
        }

        .vehicle-section {
            padding: 32px 10px;
            border-top: 1px solid $border-color;
        }

        .vehicle__rules .overview-list-item {
            align-items: center;
            margin-bottom: 8px;
            &__label {
                color: $primary-text-color;
            }
        }
    }

    &__sidebar {
        display: none;
        flex: 0 0 28%;
        max-width: 28%;

        @include lg {
            display: block;
        }
    }

    .summary-bullets {
        width: 100%;
        flex-wrap: wrap;

        li:not(:last-child) {
            margin-right: 20px;
            position: relative;

            &:after {
                content: '';
                width: 4px;
                height: 4px;
                border-radius: 50%;
                background: $primary;
                position: absolute;
                top: 50%;
                right: -12px;
                transform: translateY(-50%);
            }
        }
    }

    &__summary-bullets {
        display: none;

        @include lg {
            display: flex;
            margin-top: 16px;
        }
    }

    &__summary-insurance {
        display: flex;
        flex-direction: column;
        text-align: left;
        padding: 20px 24px;
        border: 1px solid $border-color;
        margin-top: 32px;
        border-radius: $default-border-radius;

        &__heading {
            display: flex;
            align-items: center;
            @extend %heading-3;
            margin-bottom: 24px;

            .logo {
                width: 160px;
                margin-left: 10px;
                margin-top: -5px;
            }
        }

        &__content {
            display: flex;
            align-items: center;
            margin-bottom: 16px;
            color: $darkest-gray;
            @include font-size(16px);

            .icon-wrapper {
                max-width: 34px;
                width: 34px;
                margin-right: 24px;

                .svg-icon {
                    width: 32px;
                    height: 32px;
                }
            }

            &:last-of-type {
                margin-bottom: 0;
            }
        }
    }

    &__map {
        height: 400px;
    }

    &__location.vehicle-location {
        span {
            font-weight: normal;
        }
    }

    &__location_notice {
        margin-bottom: 20px;
    }

    &__related.section {
        padding: 90px 10px;
        background: $cream-white;
    }

    &__overview,
    &__rules,
    &__prices {
        .overview-list-item {
            .lang-fi & {
                flex-direction: column;

                &__label {
                    white-space: initial;
                    margin-right: 0;
                }
            }
        }
    }

    &__top-info {
        display: flex;
        flex-direction: column;
        border-top: 1px solid $border-color;
        padding: 32px 5px;

        &__item {
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            margin-bottom: 24px;

            &:last-of-type {
                margin-bottom: 0;
            }

            .item__icon {
                min-width: 30px;
                margin-right: 24px;
                .svg-icon {
                    width: 24px;
                    height: 24px;
                }
            }

            .item__content {
                text-align: left;
                @include font-size(16px);
                line-height: 24px;

                .title {
                    margin-bottom: 2px;
                    font-weight: 600;
                }

                .description {
                    color: $secondary-text-color;
                }
            }
        }
    }
}
</style>

<style lang="scss" scoped>
@import '@/sass/_variables.scss';

.vehicle {
    @media (min-width: $bp-lg-min) and (max-width: 1340px) {
        &__sidebar {
            ::v-deep .date-range__dates-wrapper {
                overflow: hidden;
            }

            ::v-deep .date-range__dates-wrapper__date {
                @include font-size(14px);
                overflow: hidden;
                text-overflow: ellipsis;
            }

            ::v-deep .date-range__dates-wrapper__separator {
                margin: 0;
                min-width: 18px;
            }

            ::v-deep .date-range__datepicker-icon {
                margin-right: 8px;
            }
        }

        &__booking-request {
            @include font-size(16px);
        }
    }

    @media (min-width: 1200px) and (max-width: 1340px) {
        ::v-deep .date-range__dates-wrapper__date {
            @include font-size(15px);
        }
    }
}
</style>
